import { getWebInstrumentations, initializeFaro } from '@grafana/faro-web-sdk'
import { TracingInstrumentation } from '@grafana/faro-web-tracing'
import config from './config'

const SESSION_STORAGE_KEY = 'smartresilience.observability.session_id'
const EVENT_DOMAIN = 'smartresilience'
const PLACEHOLDER_PREFIX = '__GRAFANA_'

let faroInstance = null
let initialized = false

const isPlaceholder = value => !value || String(value).startsWith(PLACEHOLDER_PREFIX)
const DEFAULT_TRACE_SAMPLE_RATE = 1

const escapeRegExp = value => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const stringify = value => {
  if (value === undefined || value === null) {
    return undefined
  }

  if (typeof value === 'string') {
    return value
  }

  return String(value)
}

const sanitizeAttributes = attributes =>
  Object.entries(attributes ?? {}).reduce((acc, [key, value]) => {
    const nextValue = stringify(value)
    if (nextValue !== undefined) {
      acc[key] = nextValue
    }
    return acc
  }, {})

const parseUrlPath = rawUrl => {
  if (!rawUrl) {
    return undefined
  }

  try {
    return new URL(rawUrl, window.location.origin).pathname
  } catch (_error) {
    return rawUrl
  }
}

const resolveBusinessEvent = ({ method, url, statusCode }) => {
  const normalizedMethod = String(method ?? '').toUpperCase()
  const path = parseUrlPath(url) ?? ''
  if (normalizedMethod !== 'POST' || statusCode >= 400) {
    return null
  }

  if (path.includes('/locations/create')) {
    return 'property_create'
  }

  // if (path.includes('/alerts/configurations')) {
  //   return 'alert_config_create'
  // }

  // if (path.includes('/forms') && !path.includes('/signed-url')) {
  //   return 'form_submit'
  // }

  // if (path.includes('/analytics/cc/metrics/export')) {
  //   return 'climate_export_start'
  // }

  return null
}

const generateId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }

  return `session-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

export const getSessionId = () => {
  if (typeof window === 'undefined') {
    return 'server'
  }

  const existingId = window.sessionStorage.getItem(SESSION_STORAGE_KEY)
  if (existingId) {
    return existingId
  }

  const nextId = generateId()
  window.sessionStorage.setItem(SESSION_STORAGE_KEY, nextId)
  return nextId
}

export const getClientVersion = () => {
  const version = stringify(config.appVersion)
  const buildSha = stringify(config.buildSha)
  if (version && buildSha) {
    return `${version}+${buildSha}`
  }

  return version || buildSha || 'unknown'
}

const getRelease = () => config.release || getClientVersion()

export const isObservabilityEnabled = () =>
  !isPlaceholder(config.faroCollectorUrl)

const normalizeTraceSampleRate = rawValue => {
  const value = Number(rawValue)
  if (!Number.isFinite(value)) {
    return DEFAULT_TRACE_SAMPLE_RATE
  }

  return Math.max(0, Math.min(1, value))
}

const hashValue = value => {
  let hash = 0

  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) >>> 0
  }

  return hash / 0xffffffff
}

const isTraceSessionSampled = sessionId => {
  const sampleRate = normalizeTraceSampleRate(config.faroTraceSampleRate)
  if (sampleRate <= 0) {
    return false
  }

  if (sampleRate >= 1) {
    return true
  }

  return hashValue(sessionId) < sampleRate
}

const resolveTracePropagationTargets = () => {
  const targets = new Set([window.location.origin])
  const backendEndpoint = stringify(config.backendEndpoint)
  const patterns = []

  if (backendEndpoint) {
    try {
      const backendUrl = new URL(backendEndpoint, window.location.origin)
      targets.add(backendUrl.origin)
      patterns.push(
        new RegExp(`^${escapeRegExp(backendUrl.href.replace(/\/+$/, ''))}(?:/|$)`),
      )
    } catch (_error) {
      patterns.push(
        new RegExp(`^${escapeRegExp(backendEndpoint.replace(/\/+$/, ''))}(?:/|$)`),
      )
    }
  }

  for (const origin of targets) {
    patterns.push(new RegExp(`^${escapeRegExp(origin)}(?:/|$)`))
  }

  return patterns
}

const getFaro = () => {
  if (!initialized) {
    initializeObservability()
  }

  return faroInstance
}

export const initializeObservability = () => {
  if (initialized) {
    return faroInstance
  }

  initialized = true
  if (!isObservabilityEnabled()) {
    return null
  }

  const sessionId = getSessionId()
  const traceSampleRate = normalizeTraceSampleRate(config.faroTraceSampleRate)
  const isTraceSampled = isTraceSessionSampled(sessionId)

  faroInstance = initializeFaro({
    url: config.faroCollectorUrl,
    app: {
      name: config.faroAppName,
      environment: config.stage,
      release: getRelease(),
      version: stringify(config.appVersion),
    },
    instrumentations: [
      ...getWebInstrumentations({
        captureConsole: true,
      }),
      new TracingInstrumentation({
        resourceAttributes: {
          'service.namespace': config.faroAppNamespace,
          'deployment.environment.name': config.stage,
          'service.version': getClientVersion(),
        },
        instrumentationOptions: {
          propagateTraceHeaderCorsUrls: resolveTracePropagationTargets(),
        },
      }),
    ],
  })

  faroInstance.api.setSession({
    id: sessionId,
    attributes: sanitizeAttributes({
      isSampled: isTraceSampled,
      trace_sample_rate: traceSampleRate,
      environment: config.stage,
      release: getRelease(),
      build_sha: config.buildSha,
      service_namespace: config.faroAppNamespace,
    }),
  })
  faroInstance.api.setView({
    name: window.location.pathname,
  })

  return faroInstance
}

export const trackJourney = (name, attributes = {}) => {
  const faro = getFaro()
  if (!faro) {
    return
  }

  faro.api.pushEvent(name, sanitizeAttributes(attributes), EVENT_DOMAIN)
}

export const trackViewChange = pathname => {
  const faro = getFaro()
  if (!faro || !pathname) {
    return
  }

  faro.api.setView({
    name: pathname,
  })
  faro.api.pushEvent(
    'route_change',
    sanitizeAttributes({
      path: pathname,
      release: getRelease(),
    }),
    EVENT_DOMAIN,
  )
}

export const setObservabilityUser = user => {
  const faro = getFaro()
  if (!faro || !user) {
    return
  }

  faro.api.setUser({
    id: stringify(user.id),
    fullName: stringify(user.full_name),
    roles: stringify(user?.role?.name ?? user?.role?.id),
    attributes: sanitizeAttributes({
      organisation_id: user?.organisation?.id,
      environment: config.stage,
      release: getRelease(),
      build_sha: config.buildSha,
      service_namespace: config.faroAppNamespace,
    }),
  })
}

export const resetObservabilityUser = () => {
  const faro = getFaro()
  if (!faro) {
    return
  }

  faro.api.resetUser()
}

export const captureApiSuccess = payload => {
  const faro = getFaro()
  if (!faro) {
    return
  }

  const businessEvent = resolveBusinessEvent(payload)
  if (!businessEvent) {
    return
  }

  faro.api.pushEvent(
    businessEvent,
    sanitizeAttributes({
      method: payload.method,
      path: parseUrlPath(payload.url),
      status_code: payload.statusCode,
      duration_ms: payload.durationMs,
      request_id: payload.requestId,
      session_id: getSessionId(),
    }),
    EVENT_DOMAIN,
  )
}

export const captureApiFailure = payload => {
  const faro = getFaro()
  if (!faro) {
    return
  }

  faro.api.pushEvent(
    'api_request_failed',
    sanitizeAttributes({
      method: payload.method,
      path: parseUrlPath(payload.url),
      status_code: payload.statusCode,
      duration_ms: payload.durationMs,
      request_id: payload.requestId,
      error: payload.error,
      session_id: getSessionId(),
    }),
    EVENT_DOMAIN,
  )
}
